import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import {
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Box,
  RadioGroup,
  IconButton,
  FormControlLabel,
  Radio,
  TextField as MuiTextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  Alert
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import ShareIcon from '@mui/icons-material/Share';
import { v4 as uuidv4 } from 'uuid';
import * as signalR from '@microsoft/signalr';
import api from '../../Services/api';
import { errorManagement } from "../../Services/errorManagement";
import { CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Card, CardContent } from '@mui/material';
import { HomeAppContext } from "../Home";
import Lottie from 'lottie-react';
import waitingAnimation from '../../waitingAnimation.json';
import NavigationIcon from '@mui/icons-material/Navigation';
import { Tabs, Tab } from '@mui/material';
import { useLocation } from 'react-router-dom';

const ChatPage = () => {
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [selectedAiEngine, setSelectedAiEngine] = useState('');
  const [selectedConversationTitle, setSelectedConversationTitle] = useState(null);
  const [aiEngines, setAiEngines] = useState([]);
  const [userId, setUserId] = useState(null);
  const { homeState, homeDispatch } = useContext(HomeAppContext);
  const [groupedEngines, setGroupedEngines] = useState([]);
  const [engineFamilies, setEngineFamilies] = useState([]);
  const [selectedEngineFamily, setSelectedEngineFamily] = useState('');
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [shareType, setShareType] = useState(0);
  const [conversationToShare, setConversationToShare] = useState(null);
  const [activeTab, setActiveTab] = useState(1);
  const location = useLocation();
  const theme = useTheme();
  const isMediumDown = useMediaQuery(theme.breakpoints.down('md'));
  const [pendingConversationId, setPendingConversationId] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('conversationId');
  });

  const layoutStorageKey = 'chatLayoutPreferences';
  const defaultLayout = {
    leftPanelWidth: 60,
    chatAreaHeight: 420
  };
  const [layout, setLayout] = useState(() => {
    if (typeof window === 'undefined') {
      return defaultLayout;
    }
    try {
      const stored = window.localStorage.getItem(layoutStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          leftPanelWidth:
            typeof parsed.leftPanelWidth === 'number'
              ? parsed.leftPanelWidth
              : defaultLayout.leftPanelWidth,
          chatAreaHeight:
            typeof parsed.chatAreaHeight === 'number'
              ? parsed.chatAreaHeight
              : defaultLayout.chatAreaHeight
        };
      }
    } catch (error) {
      console.warn('Failed to read chat layout preferences', error);
    }
    return defaultLayout;
  });
  const containerRef = useRef(null);
  const leftPanelRef = useRef(null);
  const layoutOriginRef = useRef({
    startY: 0,
    startChatHeight: defaultLayout.chatAreaHeight
  });
  const [isHorizontalResizing, setIsHorizontalResizing] = useState(false);
  const [isVerticalResizing, setIsVerticalResizing] = useState(false);
  const splitHandleWidth = 12;
  const minLeftPanelWidthPx = 320;
  const minRightPanelWidthPx = 280;
  const minChatAreaHeight = 220;
  const maxChatAreaHeight = 820;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newConversationId = params.get('conversationId');
    setPendingConversationId((prev) =>
      prev === newConversationId ? prev : newConversationId
    );
  }, [location.search]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(layoutStorageKey, JSON.stringify(layout));
    } catch (error) {
      console.warn('Failed to persist chat layout preferences', error);
    }
  }, [layout]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    if (isHorizontalResizing) {
      document.body.style.cursor = 'col-resize';
    } else if (isVerticalResizing) {
      document.body.style.cursor = 'row-resize';
    } else {
      document.body.style.cursor = '';
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.cursor = '';
      }
    };
  }, [isHorizontalResizing, isVerticalResizing]);

  useEffect(() => {
    if (!(isHorizontalResizing || isVerticalResizing)) {
      return undefined;
    }

    const handlePointerMove = (event) => {
      const isTouch = event.type.startsWith('touch');
      const point = isTouch ? event.touches[0] : event;
      if (!point) {
        return;
      }
      let preventDefault = false;

      if (isHorizontalResizing && !isMediumDown) {
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (containerRect) {
          preventDefault = true;
          const availableWidth = containerRect.width - splitHandleWidth;
          if (availableWidth > minLeftPanelWidthPx + minRightPanelWidthPx) {
            const relativeX =
              point.clientX - containerRect.left - splitHandleWidth / 2;
            const clampedPx = Math.min(
              Math.max(relativeX, minLeftPanelWidthPx),
              availableWidth - minRightPanelWidthPx
            );
            const newPercent = (clampedPx / availableWidth) * 100;
            setLayout((prev) =>
              Math.abs(prev.leftPanelWidth - newPercent) < 0.1
                ? prev
                : { ...prev, leftPanelWidth: newPercent }
            );
          }
        }
      }

      if (isVerticalResizing) {
        preventDefault = true;
        const startY = layoutOriginRef.current.startY;
        const startHeight = layoutOriginRef.current.startChatHeight;
        const delta = point.clientY - startY;
        const newHeight = Math.min(
          Math.max(startHeight + delta, minChatAreaHeight),
          maxChatAreaHeight
        );
        setLayout((prev) =>
          Math.abs(prev.chatAreaHeight - newHeight) < 1
            ? prev
            : { ...prev, chatAreaHeight: newHeight }
        );
      }

      if (preventDefault) {
        event.preventDefault();
      }
    };

    const stopResizing = () => {
      setIsHorizontalResizing(false);
      setIsVerticalResizing(false);
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', stopResizing);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('touchend', stopResizing);
    window.addEventListener('touchcancel', stopResizing);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', stopResizing);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', stopResizing);
      window.removeEventListener('touchcancel', stopResizing);
    };
  }, [
    isHorizontalResizing,
    isVerticalResizing,
    isMediumDown,
    minChatAreaHeight,
    maxChatAreaHeight,
    minLeftPanelWidthPx,
    minRightPanelWidthPx,
    splitHandleWidth,
  ]);

  useEffect(() => {
    const fetchFilteredConversations = async () => {
      try {
        const filterOptions = ["all", "my", "shared"];
        const response = await api.get(`/api/chatbot/GetHistory`, {
          params: { filter: filterOptions[activeTab] },
        });
        setConversations(response.data.history);
        setUserId(response.data.userId);
      } catch (error) {
        console.error('Error fetching filtered conversations:', error);
      }
    };
  
    fetchFilteredConversations();
  }, [activeTab]);

  useEffect(() => {
    if (!pendingConversationId) {
      return;
    }
    const match = conversations.find((conv) => conv.id === pendingConversationId);
    if (match) {
      setSelectedConversationId(match.id);
      setSelectedConversationTitle(match.title);
      setSelectedAiEngine(match.aiEngine);
      setPendingConversationId(null);
    }
  }, [pendingConversationId, conversations]);

  const handleHorizontalResizeStart = (event) => {
    if (isMediumDown) {
      return;
    }
    event.preventDefault();
    const isTouch = event.type === 'touchstart';
    const point = isTouch ? event.touches[0] : event;
    if (!point) {
      return;
    }
    setIsHorizontalResizing(true);
  };

  const handleVerticalResizeStart = (event) => {
    event.preventDefault();
    const isTouch = event.type === 'touchstart';
    const point = isTouch ? event.touches[0] : event;
    if (!point) {
      return;
    }
    layoutOriginRef.current.startY = point.clientY;
    layoutOriginRef.current.startChatHeight = layout.chatAreaHeight;
    setIsVerticalResizing(true);
  };

  const handleOptionChange = (event) => {
    setShareType(event.target.value);
  };  

  const handleOpenShareDialog = (id, currentShareType) => {
    setConversationToShare(id);
    setShareType(currentShareType);
    setOpenShareDialog(true);
  };
  
  const handleCloseShareDialog = () => {
    setOpenShareDialog(false);
    setConversationToShare(null);
  };
  
  const handleConfirmShareType = async () => {
    try {
      const sharingChatbotDto = {
        sharingId: conversationToShare,
        sharingOption: shareType,
      };

      await api.put('/api/chatbot/UpdateShareType', sharingChatbotDto);
  
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationToShare ? { ...conv, shareType } : conv
        )
      );
      handleCloseShareDialog();
    } catch (error) {
      console.error('Error updating share type:', error);
    }
  };

  const messageEndRef = useRef(null);
  const messageInputRef = useRef(null);

  const aiMessageIndexRef = useRef(null);
  const connectionRef = useRef(null);
  const selectedConversationIdRef = useRef(selectedConversationId);

  const handleCancelEdit = () => {
    setEditingConversationId(null);
    setEditedTitle('');
  };  

  React.useEffect(() => {
    api
      .get("/api/login/authorized")
      .then((r) => {
        homeDispatch({
          type: "UPDATE_CURRENT_LOCATION",
          data: "Chat with AI"
        });
      })
      .catch((error) => errorManagement.formErrors(error, homeDispatch));
  }, [homeDispatch]);

  useEffect(() => {
    const fetchDefaultEngine = async () => {
      try {
        const familyDto = {
          name: "ChatGPT",
        };

        const response = await api.post('/api/ai-engines/default', familyDto);
        const defaultEngine = response.data;

        if (defaultEngine) {
          setSelectedEngineFamily(defaultEngine.engineFamily);
          setSelectedAiEngine(defaultEngine.id);
        }
      } catch (error) {
        console.error('Error fetching default AI engine:', error);
      }
    };

    const fetchAllEngines = async () => {
      try {
        const response = await api.get('/api/ai-engines/ListActives');
        const engines = response.data.items;
        setAiEngines(engines);

        const grouped = engines.reduce((acc, engine) => {
          const family = engine.engineFamily;
          if (!acc[family]) acc[family] = [];
          acc[family].push(engine);
          return acc;
        }, {});

        setGroupedEngines(grouped);
        setEngineFamilies(Object.keys(grouped));
      } catch (error) {
        console.error('Error fetching AI engines:', error);
      }
    };

    fetchDefaultEngine();
    fetchAllEngines();
  }, []); 

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(process.env.REACT_APP_BACKEND_URL + "/chathub")
      .withAutomaticReconnect()
      .build();

    connection
      .start()
      .then(() => {
        console.log('Connected to SignalR hub');      

        connection.on('ReceiveAiResponse', (messagePart) => {
          setConversations((prevConversations) => {
            const updatedConversations = prevConversations.map((conv) => {
              if (conv.id === selectedConversationIdRef.current) {
                const updatedMessages = [...conv.messages];
                const aiMessageIndex = aiMessageIndexRef.current;
                if (
                  aiMessageIndex !== null &&
                  updatedMessages[aiMessageIndex]
                ) {
                  updatedMessages[aiMessageIndex].text += messagePart;
                }
                return { ...conv, messages: updatedMessages };
              }
              return conv;
            });
            return updatedConversations;
          });
  
          if (messageInputRef.current) {
            messageInputRef.current.blur();
            setTimeout(() => {
              messageInputRef.current.focus({ preventScroll: true });
              messageInputRef.current.setSelectionRange(
                messageInputRef.current.value.length,
                messageInputRef.current.value.length
              );
            }, 50);
          }
        });
      })
      .catch((error) => console.error('SignalR Connection Error: ', error));

      connection.on('SetConversationId', (conversationId) => {
        setSelectedConversationId(conversationId);
        selectedConversationIdRef.current = conversationId;
      
        setConversations((prevConversations) => {
          return prevConversations.map((conv) => {
            if (conv.id === null) {
              return { ...conv, id: conversationId };
            }
            return conv;
          });
        });
      });  

    connectionRef.current = connection;

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
  }, []);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setNewMessage('');
    setLoading(true);
    try {
      const userMessage = { role: 'user', text: newMessage };
      const aiMessage = { role: 'ai', text: '' };

      if (!selectedConversationId) {
        const newConversationId = uuidv4();
        const firstPrompt = newMessage.trim();
        let truncatedTitle = firstPrompt.substring(0, 22);

        if (firstPrompt.length > 22) {
          const lastSpaceIndex = truncatedTitle.lastIndexOf(' ');
          if (lastSpaceIndex > 0) {
            truncatedTitle = truncatedTitle.substring(0, lastSpaceIndex);
          }
        }

        const newConversation = {
          id: newConversationId,
          title: truncatedTitle || `Conversation ${newConversationId.substring(0, 8)}`,
          messages: [userMessage, aiMessage],
          aiEngine: selectedAiEngine,
          lastActivity: new Date().toISOString(),
        };

        setConversations([newConversation, ...conversations]);
        setSelectedConversationId(newConversation.id);
        setSelectedConversationTitle(newConversation.title);

        aiMessageIndexRef.current = 1;

        await connectionRef.current.invoke('StartChat', {
          newMessage: newMessage,
          aiEngine: selectedAiEngine,
          conversationId: newConversation.id,
          role: 'user',
          conversationTitle: newConversation.title,
          userId: userId,
        });
      } else {
        const selectedConversation = conversations.find(
          (conv) => conv.id === selectedConversationId
        );
  
        if (selectedConversation.aiEngine !== selectedAiEngine) {
          const newConversationId = uuidv4();
          const firstPrompt = newMessage.trim();
          let truncatedTitle = firstPrompt.substring(0, 22);
  
          if (firstPrompt.length > 22) {
            const lastSpaceIndex = truncatedTitle.lastIndexOf(' ');
            if (lastSpaceIndex > 0) {
              truncatedTitle = truncatedTitle.substring(0, lastSpaceIndex);
            }
          }

          const newConversation = {
            id: newConversationId,
            title: truncatedTitle || `Conversation ${newConversationId.substring(0, 8)}`,
            messages: [userMessage, aiMessage],
            aiEngine: selectedAiEngine,
            lastActivity: new Date().toISOString()
          };
  
          setConversations([newConversation, ...conversations]);
          setSelectedConversationId(newConversation.id);
          setSelectedConversationTitle(newConversation.title);
  
          aiMessageIndexRef.current = 1;
  
          await connectionRef.current.invoke('StartChat', {
            newMessage: newMessage,
            aiEngine: selectedAiEngine,
            conversationId: newConversation.id,
            role: 'user',
            conversationTitle: newConversation.title,
            userId: userId,
          });
        } else {
        let aiMessageIndex;
        setConversations((prevConversations) => {
          const updatedConversations = prevConversations.map((conv) => {
            if (conv.id === selectedConversationId) {
              const updatedMessages = [...conv.messages, userMessage, aiMessage];
              aiMessageIndex = updatedMessages.length - 1;
              return { ...conv, messages: updatedMessages, lastActivity: new Date().toISOString() };
            }
            return conv;
          });
          const updatedConversation = updatedConversations.find((conv) => conv.id === selectedConversationId);
          const otherConversations = updatedConversations.filter((conv) => conv.id !== selectedConversationId);
      
          aiMessageIndexRef.current = aiMessageIndex;
          return [updatedConversation, ...otherConversations];
        });

        await connectionRef.current.invoke('StartChat', {
          newMessage: newMessage,
          aiEngine: selectedAiEngine,
          conversationId: selectedConversationId,
          role: 'user',
          conversationTitle: selectedConversationTitle,
          userId: userId,
        });
      }
    }

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = (conversationId) => {
    const selectedConversation = conversations.find(
      (conv) => conv.id === conversationId
    );
    if (selectedConversation) {
      setSelectedConversationId(conversationId);
      setSelectedConversationTitle(selectedConversation.title);
      setSelectedAiEngine(selectedConversation.aiEngine);
      console.log('Selected conversation messages:', selectedConversation.messages);
    } else {
      console.error('Conversation not found');
    }
  };

  const handleNewConversation = async () => {
    try {
      const familyDto = {
        name: selectedEngineFamily,
      };  

      const response = await api.post(`/api/ai-engines/default`, familyDto);
      const defaultEngine = response.data;
  
      if (defaultEngine) {
        setSelectedAiEngine(defaultEngine.id);
      }
  
      setSelectedConversationId(null);
      setSelectedConversationTitle(null);
  
    } catch (error) {
      console.error('Error setting default engine for new conversation:', error);
    }
  }; 

  const handleEditConversation = (id, title) => {
    setEditingConversationId(id);
    setEditedTitle(title);
  };

  const handleSaveConversationTitle = async (id) => {
    try {
      const chatbotPut = {
        conversationId: id,
        conversationTitle: editedTitle,
      };

      await api.post(`/api/chatbot/UpdateConversationTitle`, chatbotPut);
  
      const updatedConversations = conversations.map((conv) =>
        conv.id === id ? { ...conv, title: editedTitle } : conv
      );
      setConversations(updatedConversations);
      setEditingConversationId(null);
    } catch (error) {
      console.error('Error updating conversation title:', error);
    }
  };  

  const handleOpenDeleteDialog = (id) => {
    setOpenDeleteDialog(true);
    setConversationToDelete(id);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setConversationToDelete(null);
  };

  const handleConfirmDeleteConversation = async () => {
    try {
      const chatbotDelete = {
        conversationId: conversationToDelete,
      };

      await api.post(`/api/chatbot/DeleteConversation`, chatbotDelete);
  
      const updatedConversations = conversations.filter(
        (conv) => conv.id !== conversationToDelete
      );
      setConversations(updatedConversations);
      if (selectedConversationId === conversationToDelete) {
        setSelectedConversationId(null);
      }
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };  

  const handleEngineFamilyChange = async (event) => {
    const selectedFamily = event.target.value;
    setSelectedEngineFamily(selectedFamily);
  
    try {
      const familyDto = {
        name: event.target.value,
      };  

      const response = await api.post(`/api/ai-engines/default`, familyDto);
      const defaultEngine = response.data;
  
      if (defaultEngine) {
        setSelectedAiEngine(defaultEngine.id);
      }
    } catch (error) {
      console.error('Error fetching default AI engine for family:', error);
    }
  };
  

  const handleAiEngineChange = (event) => {
    setSelectedAiEngine(event.target.value);

    setSelectedConversationId(null);
    setSelectedConversationTitle(null);
  };

  const styles = {
    container: {
      display: 'flex',
      width: '100%',
      gap: theme.spacing(2),
      alignItems: 'stretch',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column'
      }
    },
    leftPane: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(2),
      boxSizing: 'border-box',
      flexShrink: 0,
      minWidth: minLeftPanelWidthPx
    },
    inlineSidebar: {
      marginTop: theme.spacing(3),
      width: '100%'
    },
    chatShell: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1
    },
    chatBox: {
      flexGrow: 0,
      flexShrink: 0,
      overflowY: 'auto',
      borderRadius: '10px',
      backgroundColor: theme.palette.mode === 'dark'
        ? '#333'
        : theme.palette.background.paper,
      boxShadow: theme.palette.mode === 'light'
        ? '0px 4px 12px rgba(0,0,0,0.12)'
        : '0px 2px 10px rgba(0,0,0,0.5)',
      padding: theme.spacing(2)
    },
    animationContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      textAlign: 'center'
    },
    verticalResizeHandle: {
      height: 12,
      margin: theme.spacing(1.5, 0),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.12)'
        : 'rgba(0,0,0,0.08)',
      cursor: 'row-resize',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    verticalResizeGrip: {
      width: '40%',
      height: 4,
      borderRadius: 999,
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.45)'
        : 'rgba(0,0,0,0.35)'
    },
    inputArea: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      borderRadius: '12px',
      alignItems: 'center',
      boxShadow: theme.palette.mode === 'light'
        ? '0px 4px 12px rgba(0,0,0,0.12)'
        : '0px 2px 12px rgba(0,0,0,0.5)'
    },
    sendButton: {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      height: 48,
      width: 48,
      minWidth: 48,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    resizeHandle: {
      width: splitHandleWidth,
      cursor: 'col-resize',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.08)'
    },
    resizeHandleGrip: {
      width: 4,
      height: '35%',
      borderRadius: 999,
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.45)'
        : 'rgba(0,0,0,0.35)'
    },
    rightPane: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(2),
      boxSizing: 'border-box',
      flexGrow: 1,
      minWidth: minRightPanelWidthPx,
      maxWidth: 460
    },
    sidebarContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      flexGrow: 1
    },
    historyListHead: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.palette.mode === 'light'
        ? '0px 4px 12px rgba(0,0,0,0.08)'
        : '0px 2px 12px rgba(0,0,0,0.5)'
    },
    historyList: {
      padding: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
      overflowY: 'auto',
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.palette.mode === 'light'
        ? '0px 4px 12px rgba(0,0,0,0.08)'
        : '0px 2px 12px rgba(0,0,0,0.5)'
    },
    newConversationButton: {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      alignSelf: 'flex-start'
    },
    messageRow: (isUser) => ({
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: theme.spacing(1.5)
    }),
    messageBubble: (isUser) => ({
      maxWidth: '75%',
      padding: theme.spacing(1.5),
      borderRadius: '15px',
      backgroundColor: isUser
        ? theme.palette.primary.main
        : theme.palette.background.default,
      color: isUser ? '#fff' : theme.palette.text.primary,
      boxShadow: '0px 2px 6px rgba(0,0,0,0.15)',
      whiteSpace: 'pre-wrap'
    })
  };

  const normalizedLeftPercent = Math.min(Math.max(layout.leftPanelWidth, 30), 75);
  const shouldInlineSidebar = isMediumDown;
  const leftPaneFlexBasis = shouldInlineSidebar
    ? '100%'
    : `calc(${normalizedLeftPercent}% - ${splitHandleWidth / 2}px)`;
  const rightPaneFlexBasis = shouldInlineSidebar
    ? '100%'
    : `calc(${100 - normalizedLeftPercent}% - ${splitHandleWidth / 2}px)`;
  const chatBoxHeight = Math.min(
    Math.max(layout.chatAreaHeight, minChatAreaHeight),
    maxChatAreaHeight
  );
  const historyMaxHeight = shouldInlineSidebar ? '40vh' : 'calc(100vh - 360px)';

  const selectedConversation = conversations.find(
    (conv) => conv.id === selectedConversationId
  );
  const messages = useMemo(
    () => (selectedConversation ? selectedConversation.messages : []),
    [selectedConversation]
  );
  const isReadOnly = selectedConversation?.userId !== homeState.userProfile.userId && selectedConversation?.shareType === 1;

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConversationId, messages]);  

  const conversationHistorySection = (
    <Box style={styles.sidebarContent}>
      <Card>
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Select AI Engine Family</InputLabel>
            <Select
              value={selectedEngineFamily}
              onChange={handleEngineFamilyChange}
              label="Select AI Engine Family"
            >
              {engineFamilies.map((family) => (
                <MenuItem key={family} value={family}>
                  {family}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            fullWidth
            disabled={!selectedEngineFamily}
            style={{ marginTop: 20 }}
          >
            <InputLabel>Select AI Engine</InputLabel>
            <Select
              value={selectedAiEngine}
              onChange={handleAiEngineChange}
              label="Select AI Engine"
            >
              {selectedEngineFamily &&
                groupedEngines[selectedEngineFamily]?.map((engine) => (
                  <MenuItem key={engine.id} value={engine.id}>
                    {engine.engineName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>
      <Box style={styles.historyListHead}>
        <Typography variant="h6" gutterBottom>
          Conversation History
        </Typography>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="All" />
          <Tab label="Created by Me" />
          <Tab label="Shared with Me" />
        </Tabs>
      </Box>
      <List style={{ ...styles.historyList, maxHeight: historyMaxHeight }}>
        {conversations.map((conv) => {
          const isSelected = selectedConversationId === conv.id;
          const canEdit =
            conv.userId === homeState.userProfile.userId ||
            (conv.userId !== homeState.userProfile.userId && conv.shareType === 2);
          const canManage = conv.userId === homeState.userProfile.userId;
          return (
            <ListItem
              button
              key={conv.id}
              onClick={() => handleSelectConversation(conv.id)}
              style={{
                borderRadius: 6,
                marginBottom: 4,
                backgroundColor: isSelected
                  ? theme.palette.primary.main
                  : undefined,
                color: isSelected ? '#fff' : undefined
              }}
            >
              {editingConversationId === conv.id ? (
                <>
                  <MuiTextField
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSaveConversationTitle(conv.id);
                    }}
                    size="small"
                    color="inherit"
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      handleCancelEdit();
                    }}
                    size="small"
                    color="inherit"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <ListItemText
                    primary={conv.title || `Conversation ${conv.id}`}
                    secondary={`${
                      conv.lastActivity
                        ? new Date(conv.lastActivity).toLocaleDateString()
                        : new Date().toLocaleDateString()
                    } - ${
                      aiEngines.find((engine) => engine.id === conv.aiEngine)?.engineName ||
                      'Unknown'
                    }`}
                  />
                  {canEdit && (
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        handleEditConversation(conv.id, conv.title);
                      }}
                      size="small"
                      color="inherit"
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  {canManage && (
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenDeleteDialog(conv.id);
                      }}
                      size="small"
                      color="inherit"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                  {canManage && (
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenShareDialog(conv.id, conv.shareType);
                      }}
                      size="small"
                      color="inherit"
                    >
                      <ShareIcon />
                    </IconButton>
                  )}
                </>
              )}
            </ListItem>
          );
        })}
      </List>
      <Button
        variant="contained"
        style={styles.newConversationButton}
        onClick={handleNewConversation}
      >
        New Conversation
      </Button>
    </Box>
  );

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        The legacy “Chat with AI” page will be retired soon. Please use the new floating AI
        chat widget (bottom-right) for a better, always-available experience.
      </Alert>
      <Box
        ref={containerRef}
        style={{
          ...styles.container,
          flexDirection: shouldInlineSidebar ? 'column' : 'row'
        }}
      >
        <Box
          ref={leftPanelRef}
          style={{
            ...styles.leftPane,
            flexBasis: leftPaneFlexBasis,
            width: shouldInlineSidebar ? '100%' : undefined
          }}
        >
          <Box style={styles.chatShell}>
            <Paper style={{ ...styles.chatBox, height: chatBoxHeight }}>
              {messages.length === 0 ? (
                <Box style={styles.animationContainer}>
                  <Lottie animationData={waitingAnimation} loop />
                  <Typography variant="h6" style={{ marginTop: 20 }}>
                    Waiting for your prompt...
                  </Typography>
                </Box>
              ) : (
                messages.map((msg, idx) => (
                  <Box
                    key={idx}
                    style={styles.messageRow(
                      msg.role === 'user' || msg.role === 'User'
                    )}
                  >
                    <Box
                      style={styles.messageBubble(
                        msg.role === 'user' || msg.role === 'User'
                      )}
                    >
                      {msg.text}
                    </Box>
                  </Box>
                ))
              )}
              <div ref={messageEndRef} />
            </Paper>
            <Box
              style={styles.verticalResizeHandle}
              onMouseDown={handleVerticalResizeStart}
              onTouchStart={handleVerticalResizeStart}
            >
              <Box style={styles.verticalResizeGrip} />
            </Box>
            <Box style={styles.inputArea}>
              <TextField
                variant="outlined"
                placeholder={
                  isReadOnly
                    ? 'This prompt is public read-only, so you cannot edit it.'
                    : 'Ask me anything...'
                }
                fullWidth
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                multiline
                minRows={3}
                disabled={loading || isReadOnly}
                inputRef={messageInputRef}
                style={{ flex: '1 1 220px' }}
              />
              <Button
                variant="contained"
                disabled={loading || isReadOnly}
                style={styles.sendButton}
                onClick={handleSendMessage}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <NavigationIcon />
                )}
              </Button>
            </Box>
          </Box>
          {shouldInlineSidebar && (
            <Box style={styles.inlineSidebar}>{conversationHistorySection}</Box>
          )}
        </Box>
        {!shouldInlineSidebar && (
          <>
            <Box
              style={styles.resizeHandle}
              onMouseDown={handleHorizontalResizeStart}
              onTouchStart={handleHorizontalResizeStart}
            >
              <Box style={styles.resizeHandleGrip} />
            </Box>
            <Box
              style={{
                ...styles.rightPane,
                flexBasis: rightPaneFlexBasis
              }}
            >
              {conversationHistorySection}
            </Box>
          </>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this conversation? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDeleteConversation} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openShareDialog} onClose={handleCloseShareDialog}>
      <DialogTitle>Share Settings</DialogTitle>
      <DialogContent>
        <Typography>Select the sharing option:</Typography>
        <RadioGroup
          value={shareType}
          onChange={handleOptionChange}
          aria-label="sharing options"
        >
          <FormControlLabel value={0} control={<Radio />} label="Private" />
          <Box pl={4} mb={2}>
            <Typography variant="body2" color="textSecondary">
              This is the default state and means that nobody else can see this conversation, other than the creator.
            </Typography>
          </Box>

          <FormControlLabel value={1} control={<Radio />} label="Public Read-Only" />
          <Box pl={4} mb={2}>
            <Typography variant="body2" color="textSecondary">
              This conversation is shared, but only the creator can edit it.
            </Typography>
          </Box>

          <FormControlLabel value={2} control={<Radio />} label="Public" />
          <Box pl={4} mb={2}>
            <Typography variant="body2" color="textSecondary">
              This conversation can be edited by anyone. When they save it, all changes will be saved back and available to all users.
            </Typography>
          </Box>
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseShareDialog} color="secondary">Cancel</Button>
        <Button onClick={handleConfirmShareType} color="primary">Save</Button>
      </DialogActions>
    </Dialog>

    </>
  );
};

export default ChatPage;

